/**
 * Techline case data types that describe the domain shapes returned by the NorthStar API.
 *
 * These types were previously imported from `@packages/models`; they are now defined natively
 * in `@packages/clients` so the clients package has no dependency on the models package.
 */

/**
 * The submission status of a worksheet answer set within a Techline case.
 */
export enum RetailerWorksheetAnswerStatus {
    Draft = 'DRAFT',
    Submitted = 'SUBMITTED',
    Uploaded = 'UPLOADED',
}

/**
 * Status codes representing the lifecycle stages of a Techline case in NorthStar.
 */
export enum TechlineStatusCode {
    AUTO_CLOSED = 'AUTO CLOSED',
    CLOSE_RESOLVED = 'CLOSE RESOLVED',
    CLOSED = 'CLOSED',
    CLOSED_AUTO_CLOSED = 'CLOSED-AUTO CLOSED',
    CLOSED_RESOLVED = 'CLOSED-RESOLVED',
    DRAFT = 'DRAFT',
    ESCALATED = 'ESCALATED',
    IN_REVIEW = 'IN REVIEW',
    LEGACY_OPEN = 'OPEN',
    LEGACY_OPEN_ESCALATED = 'OPEN-ESCALATED',
    LEGACY_PENDING_TECHLINE = 'PENDING-TECHLINE',
    NS_CLOSED_APPROVED = 'CLOSED-APPROVED',
    NS_CLOSED_CREATED_IN_ERROR = 'CLOSED-CREATED IN ERROR',

    NS_CLOSED_DUPLICATE = 'CLOSED-DUPLICATE',
    NS_CLOSED_REFERRED = 'CLOSED-REFERRED',
    NS_CLOSED_REJECTED = 'CLOSED-REJECTED',
    NS_CLOSED_RESOLUTION_UNKNOWN = 'CLOSED-RESOLUTION UNKNOWN',
    NS_DEALER_UPDATE = 'DEALER UPDATE',
    NS_PENDING_CM = 'PENDING-C/M',
    NS_PENDING_DEALER_UPDATE = 'PENDING-DEALER UPDATE',
    NS_PENDING_PART_DELAY = 'PENDING-PART DELAY',
    NS_PENDING_RESEARCH = 'PENDING-RESEARCH',
    NS_TECHSHARE_SOFT_CLOSE = 'TechShare Soft Close',
    OPEN_ESCALATED = 'OPEN ESCALATED',
    PENDING_TECHLINE = 'PENDING TECHLINE',
    UPDATE_REQUESTED = 'UPDATE REQUESTED',
}

/**
 * A case type detail entry returned alongside a Techline case from NorthStar.
 */
export type CaseTypeDetail = {
    /** Human-readable description of the case type, if available. */
    caseTypeDescription?: string;

    /** The unique identifier of this case type. */
    caseTypeId: string;

    /** The display name of the case type. */
    caseTypeName: string;

    /** The NorthStar source ID for this case type. */
    caseTypeSourceId: string;

    /** The NorthStar state code for this case type. */
    stateCode: string;

    /** The NorthStar status code for this case type. */
    statusCode: string;
};

/**
 * Response DTO for retrieving Techline case details from the NorthStar API.
 *
 * The `caseDetails` field has a union shape because the NorthStar API may return the
 * case data directly or wrapped in an envelope with status/message metadata.
 *
 * Endpoint: GET /northstar/retrieveCase/{caseNumber}/v1
 */
export type GetCaseDetailsResponse = {
    /** The case asset details (untyped; structure varies by response). */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    caseAssetsDetails: any;

    /**
     * The case details, either as a direct `Retailer` object or wrapped in an envelope
     * containing status metadata.
     */
    caseDetails:
        | Retailer
        | {
              /** A status message from the API. */
              message: string;

              /** The case data nested inside the envelope. */
              results: Retailer;

              /** The status string from the API. */
              status: string;

              /** The HTTP-like status code from the API. */
              statusCode: number;
          };

    /** The list of case type details, if available. */
    caseTypeDetails?: CaseTypeDetail[];

    /** The list of VIN details associated with the case. */
    vinDetails: VinDetail[];
};

/**
 * The full data shape for a Techline case as returned by the NorthStar API.
 *
 * Contains case details, vehicle info, retailer profiles, worksheet submissions,
 * and safety flags. Despite the legacy name "Retailer" in `@packages/models`, this
 * represents the complete case record.
 */
export type Retailer = {
    /** Whether the vehicle was involved in an accident. */
    accidentFlag: boolean;

    /** Whether an airbag rupture occurred. */
    airbagRuptureFlag: boolean;

    /** The asset (vehicle) associated with the case, containing engine and transmission info. */
    asset: {
        /** The asset identifier. */
        assetId: string;

        /** The engine identifier. */
        enginId: string;

        /** The transmission number. */
        transmissionNumber: string;
    };

    /** The unique case identifier in NorthStar. */
    caseId: string;

    /** The list of case types associated with this case. */
    caseTypes: {
        /** The case type name. */
        caseType: string;

        /** The NorthStar source ID for this case type. */
        caseTypeSourceId: string;

        /** Whether this is the primary case type ("true" or "false" as string). */
        isPrimary: string;
    }[];

    /** The customer's description of their concern. */
    customerConcern: string;

    /** Whether a death was associated with this case. */
    deathFlag: boolean;

    /** Diagnostic trouble codes associated with the case. */
    diagnosticTroubleCodes: {
        /** The DTC value. */
        diagnosticTroubleCode: string;

        /** Whether freeze frame data is available in SSM. */
        freezeFrameDataInSSM: boolean;

        /** Whether this is the primary DTC. */
        isPrimary: boolean;
    }[];

    /** Whether the condition improved after repair. */
    didTheConditionImprove: string;

    /** The fail code for the case. */
    failCode: string;

    /** Whether a fire occurred. */
    fireFlag: boolean;

    /** Whether the technician has been able to duplicate the condition. */
    hasBeenAbleToDuplicate: boolean;

    /** Whether an injury was associated with this case. */
    injuryFlag: boolean;

    /** The current vehicle mileage. */
    mileage: number;

    /** The timestamp when the case was last modified (ISO 8601). */
    modifiedOn: string;

    /** Human-readable description of the last modification time. */
    modifiedOnDescription: string;

    /** Description of previous diagnosis and repair attempts. */
    previousDiagnosisAndRepairs: string;

    /** The vehicle mileage at the time of the previous repair order. */
    previousMileage: number;

    /** The previous repair order number. */
    previousRONumber: string;

    /** The primary case type name, if set. */
    primaryCaseType?: string;

    /** The product (vehicle) details for this case. */
    product: {
        /** The vehicle color category. */
        colorCategory: string;

        /** The vehicle model name. */
        model: string;

        /** The vehicle model number. */
        modelNumber: string;

        /** The vehicle model year. */
        modelYear: string;

        /** The vehicle option code. */
        optionCode: string;

        /** The product identifier. */
        productId: string;
    };

    /** Description of any property damage. */
    propertyDamage: string;

    /** The repair code for the case. */
    repairCode: string;

    /** The repair order number. */
    repairOrderNumber: string;

    /** The repair order open date. */
    repairOrderOpenDate: string;

    /** The retailer profiles associated with this case. */
    retailerProfiles: RetailerProfile[];

    /** Deduplicated retailer profiles, if available. */
    retailerUniqueProfiles?: RetailerProfile[];

    /** Whether a rollover occurred. */
    rolloverFlag: boolean;

    /** Combined description of rollover, fire, accident, injury, death, and damage. */
    rolloverFireAccidentInjuryDeathDamage: string;

    /** The fail code section. */
    section: string;

    /** The NorthStar state code for the case. */
    stateCode: number;

    /** The NorthStar status code for the case. */
    statusCode: number;

    /** The fail code sub-section. */
    subSection: string;

    /** The list of symptoms associated with the case. */
    symptoms: unknown[];

    /** The closure description provided by TechShare. */
    techShareCaseClosureDescription: string;

    /** The current Techline status of the case. */
    techlineStatusCode: TechlineStatusCode;

    /** The 5 steps performed before attempting failure duplication. */
    technician5StepsBeforeFailureDuplication: string;

    /** The technician's description of the issue. */
    technicianDescription: string;

    /** Whether the vehicle has been brought in before for this issue. */
    vehicleHasBeenInBefore: boolean;

    /** Whether the vehicle was repaired. */
    wasTheVehicleRepaired: boolean;

    /** Whether parts were replaced during repair. */
    werePartsReplaced: boolean;

    /** Description of what parts were replaced. */
    whatPartsWereReplaced: string;

    /** Grouped worksheet submissions organized by case group. */
    WorksheetSubmissionGroups?: RetailerWorksheetSubmissionGroup[];

    /** Flat list of worksheet submissions associated with this case. */
    worksheetSubmissions: RetailerWorksheetSubmission[];
};

/**
 * A retailer profile associated with a Techline case, describing the dealership
 * and user that created or is assigned to the case.
 */
export type RetailerProfile = {
    /** The city where the retailer is located. */
    city: string;

    /** The timestamp when this profile was created (ISO 8601). */
    createdOn: string;

    /** The district code for the retailer. */
    districtCode: string;

    /** The unique identifier of the district. */
    districtId: string;

    /** Whether this is the primary retailer profile for the case. */
    isPrimary: boolean;

    /** The display name of the retailer. */
    name: string;

    /** The postal/ZIP code of the retailer. */
    postalCode: string;

    /** The region code for the retailer. */
    regionCode: string;

    /** The unique identifier of the region. */
    regionId: string;

    /** The retailer's unique number identifier. */
    retailerNumber: string;

    /** The unique identifier of this retailer profile. */
    retailerProfileId: string;

    /** The SOA user ID, if available. */
    soaUserId?: string;

    /** The state where the retailer is located. */
    state: string;

    /** The username associated with this retailer profile. */
    userName: string;

    /** The zone code for the retailer. */
    zoneCode: string;

    /** The unique identifier of the zone. */
    zoneId: string;
};

/**
 * A worksheet submission entry associated with a Techline case.
 *
 * Describes a single worksheet submission's metadata as returned inside the
 * `Retailer.worksheetSubmissions` array from NorthStar.
 */
export type RetailerWorksheetSubmission = {
    /** The timestamp when the submission was last updated (ISO 8601). */
    lastUpdatedTimestamp: string;

    /** Human-readable display string for the last-updated timestamp. */
    lastUpdatedTimestampForDisplay: string;

    /** The retailer number that owns this submission. */
    retailerNumber: string;

    /** The submission status (DRAFT, SUBMITTED, or UPLOADED). */
    status: RetailerWorksheetAnswerStatus;

    /** The NorthStar status code for the submission. */
    statusCode: string;

    /** The unique identifier of this worksheet submission. */
    submissionId: string;

    /** Display name of the user who last modified the submission. */
    userDisplayName: string;

    /** The username of the user who last modified the submission. */
    userName: string;

    /** The case type associated with this worksheet. */
    worksheetCaseType: string;

    /** The NorthStar source ID for the worksheet case type. */
    worksheetCaseTypeSourceId: string;

    /** The ID of the worksheet definition this submission is based on. */
    worksheetId: string;

    /** The display title of the worksheet. */
    worksheetTitle: string;
};

/**
 * A group of worksheet submissions organized by case group (e.g. Body, Powertrain).
 *
 * Returned inside `Retailer.WorksheetSubmissionGroups` from the NorthStar API.
 */
export type RetailerWorksheetSubmissionGroup = {
    /** A description of the worksheet case group. */
    groupDescription: string;

    /** The display name of the worksheet case group. */
    groupName: string;

    /** The unique identifier of the worksheet case group. */
    worksheetCaseGroupId: string;

    /** The list of worksheet submission titles within this group. */
    worksheetSubmissionTitles: RetailerWorksheetSubmissionTitle[];
};

/**
 * A title entry within a worksheet submission group, summarizing a single
 * worksheet submission inside a group.
 */
export type RetailerWorksheetSubmissionTitle = {
    /** The timestamp when the submission was last updated (ISO 8601). */
    lastUpdatedTimestamp: string;

    /** The status of the worksheet submission (e.g. DRAFT, SUBMITTED). */
    status: string;

    /** The unique identifier of this worksheet submission. */
    submissionId: string;

    /** Display name of the user who owns the submission. */
    userDisplayName: string;

    /** A description of the worksheet. */
    worksheetDescription: string;

    /** The ID of the worksheet definition. */
    worksheetId: string;

    /** The display title of the worksheet. */
    worksheetTitle: string;
};

/**
 * Vehicle identification details returned alongside a Techline case from NorthStar.
 */
export type VinDetail = {
    /** The vehicle color category. */
    colorCategory: string;

    /** Human-readable description of the vehicle color. */
    colorDescription: string;

    /** The emission specification. */
    emissionSpec: string;

    /** The engine identifier. */
    engineId: string;

    /** The engine displacement size. */
    engineSize: number;

    /** The vehicle model name. */
    model: string;

    /** The vehicle model number. */
    modelNumber: string;

    /** The vehicle model year. */
    modelYear: number;

    /** The vehicle option code. */
    optionCode: string;

    /** The vehicle production date. */
    productionDate: string;

    /** The transmission type description. */
    transmissionType: string;

    /** Human-readable description of the trim level. */
    trimDescription: string;

    /** The turbo indicator value. */
    turboIndicator: string;

    /** The vehicle transmission number. */
    vehicleTransmissionNumber: string;

    /** The Vehicle Identification Number. */
    vin: string;

    /** The VIN status value. */
    vinStatus: string;

    /** The warranty start date. */
    warrantyStartDt: string;
};
